
  import { computed, defineComponent, onMounted, PropType, reactive, ref, toRefs, watch } from 'vue'
  import useProductListState, { ProductListHandlers } from '@/pages/Manage/Catalogues/composables/useProductListState'
  import {
    CtCatalogueStockItem,
    CtStockItem,
    CtSupplierCatalogue,
  } from '@/models/interfaces/content-type'
  import useContentModule from '@/pages/composables/useContentModule'
  import { useStore } from 'vuex'
  import { cloneDeep, debounce } from 'lodash'
  import { divideArray, uuid } from '@/utils'
  import { UUID } from '@Protium/vue-ozone-utils'
  import { ContentType } from '@/services/directr'
  import FilterableList from '@/pages/Channels/components/FilterableList.vue'
  import ProductCard from '@/pages/Channels/components/ProductCard.vue'
  import ProductCardVariant from '@/pages/Channels/components/ProductCardVariant.vue'
  import ChevronDownIcon from '@/components/Icons/ChevronDownIcon.vue'
  import ChevronRightIcon from '@/components/Icons/ChevronRightIcon.vue'
  import { date, useQuasar } from 'quasar'
  import TabNav, { TabView } from '@/components/TabNav.vue'
  import SimpleLink from '@/components/SimpleLink.vue'
  import { format } from '@/utils/template'
  import { DATE_DISPLAY_FORMAT_SHORT } from '@/constants'
  import CloseIcon from '@/components/Icons/CloseIcon.vue'
  import AddIcon from '@/components/Icons/AddIcon.vue'
  import TickIcon from '@/components/Icons/TickIcon.vue'
  import DButton from '@/components/base/DButton.vue'
  import SupplierCatalogueStockItemCard from '@/pages/WIP/components/SupplierCatalogueStockItemCard.vue'
  import { useRouter } from 'vue-router'

  import NoDataImg from '@/components/NoDataImg.vue'
import useDeviceSettings from '@/composables/useDeviceSettings'

  const DELIVERY_WARN_THRESHOLD = 10
  const DEFAULT_PRICE_LIST_ITEM = () => ({
    id: uuid(),
    label: 'Single',
    value: 0,
    quantity: 1,
    active: true
  })

  export default defineComponent({
    name: 'SupplierCatalogueStockItems',
    components: { DButton, AddIcon, TickIcon, CloseIcon, ChevronDownIcon, ChevronRightIcon, ProductCardVariant, FilterableList, ProductCard, TabNav, SimpleLink, SupplierCatalogueStockItemCard, NoDataImg },
    props: {
      linkType: {
        type: String as PropType<ContentType>,
        default: ContentType.StockItem
      },
      supplierCatalogue: {
        type: Object as PropType<CtSupplierCatalogue>,
        required: true,
      },
      tabs: {
        type: Array as PropType<Array<TabView>>,
        default: () => [
          { id: 'prices', label: 'Prices' },
          { id: 'details', label: 'Product details' }
        ]
      },
      defaultTab: {
        type: String,
        default: 'prices'
      }
    },
    emits: ['packing-resolved'],
    setup (props, { emit }) {
      const $store = useStore()
      const $quasar = useQuasar()

      const expanded = ref(false)
      const height = ref()
      const cardHeight = ref<number>()


      const queryFn = (item, chunk) => (item.sys.id && (item.sys.id.includes(chunk)))

      const contentModule = useContentModule<CtCatalogueStockItem>('CatalogueStockItem', $store)
      const { active, model, loading, fetch, mutate, requestHandler, contentType } = contentModule

      const $settings = useDeviceSettings($store)


      const {
        model: stockItemModel,
      } = useContentModule<CtStockItem>('StockItems', $store)

      void $store.dispatch('View/SetListActions', {
        remove: true,
        create: true,
        add: false,
        search: true,
        allow: { delete: true }
      })

      const setQuery = debounce((query) => {
        state.productQuery = query
      }, 300, { leading: true })

      const setFilter = debounce((filter) => {
        state.filterValue = filter
      }, 300, { leading: true })

      const saveHooks = {
        pre: async (s, d) => {
          await refreshSkuList()
          return processRemovalQueue(s, d)
        }, // re-fetch the current last sku value. We do this as close to the update point as possible to prevent errors for trying to create a duplicate sku
        beforeCreate: (item, saveState) => {
          item.sys.draft = true
          if (!saveState.ids) saveState.ids = []
          console.log(item, saveState)

          // add the newly incremented item to the start of the array
          saveState.ids.unshift(item)
          return Promise.resolve()
        },
        post: async () => {
          // reset the lhs
          diffState.lhs = cloneDeep(diffState.rhs)
          await refreshSkuList()
          return Promise.resolve()
        }
      }

      // TODO: now what?
      const draft: ProductListHandlers<CtStockItem, CtCatalogueStockItem>['draft'] = (type, stockItem, supplierCatalogue) => ({
        sys: { id: uuid(), draft: true, type },
        id: stockItem.sys.id,
        active: true,
        catalogue: supplierCatalogue.sys.id as UUID,
        deliverable_from: undefined,
        deliverable_to: undefined,
        description: stockItem.description,
        images: [],
        meta: {},
        price_list: [],
        price_lock: false,
        supplier_product_id: undefined,
        supply_chain_tracking_id: undefined,
        tax_tier: stockItem.tax_tier
      })

      const {
        state,
        applyQuery,
        processRemovalQueue,
        getCatalogueVariantIndex,
        updateProductStore,
        updateLink,
        openSearch,
        add,
        select,
        expandItem,
        addVariant,
        save,
        saveState,
        diffState,
        refreshSkuList,
        toggleRemoval,
      } = useProductListState<CtStockItem, CtCatalogueStockItem>(
        { ...props, catalogue: props.supplierCatalogue }, contentModule, $store, { saveHooks, draft }, queryFn
      )

      const stockItems = computed(() => state?.products ?? [])
      const updatedItemsByUUID = ref<Array<UUID>>([])

      function init () {
        if (props.supplierCatalogue.sys.id === undefined || props.supplierCatalogue.sys.id === null) return

        // get the sku list
        void refreshSkuList()

        // fetch all catalogue stock items matching the given supplierCatalogue sys.id
        fetch([{ field: 'catalogue', method: 'is', match: props.supplierCatalogue.sys.id }])
          .then(startingState => {
            diffState.lhs = cloneDeep(startingState)
            diffState.rhs = (startingState || [])

            // resolve the id & variants field for every stockItem id
            const uuidChunks = divideArray((startingState || [])
              .map(catalogue_stock_item => catalogue_stock_item.id)
              .flat()
              .filter(Boolean), 100)
            Promise.all(uuidChunks.map(chunk => requestHandler.get(
              ContentType.StockItem,
              chunk.filter(Boolean)
            ).then(({ items = [] }) => items)))
              .then((chunks) => {
                const items = chunks.flat(1)
                state.products = items
              }).catch(console.error)
          }).catch(console.error)

          .finally(() => {
            const lastViewed = parseInt($settings.settings.value?.supplierCatalogueLastViewedTimestamps?.[props.supplierCatalogue.sys.id] || '0')

            // create a list of which products updated_at timestamps are after the current last-viewed date
            updatedItemsByUUID.value = !lastViewed ? [] : diffState.rhs.reduce((ids, p) => {
              if (p.sys.updated_at && new Date(p.sys.updated_at).valueOf() > lastViewed) {
                ids.push(p.sys.id)
              }

              return ids
            }, [] as Array<UUID>)

            // reset last-viewed for supplier-catalogue products
            if (props.supplierCatalogue) {
              $settings.set('supplierCatalogueLastViewedTimestamps', { ...($settings.settings.value?.supplierCatalogueLastViewedTimestamps || {}), [props.supplierCatalogue.sys.id]: Date.now() })
            }
          })
      }

      onMounted(init)
      watch(() => props.supplierCatalogue, init)

      const editing = ref(false)
      const editingStockItems = ref(new Set())
      watch(
        () => editingStockItems.value.size,
        (newValue) => newValue && !editing.value && (editing.value = true)
      )

      const addStockItem = () => {
        if (!editing.value) editing.value = true
        add()
      }

      const cancelEditing = () => {
        processRemovalQueue(null, diffState)
        diffState.rhs = cloneDeep(diffState.lhs)
        editingStockItems.value.clear()
        editing.value = false
      }

      const handleSave = () => {
        void save()
        editingStockItems.value.clear()
        editing.value = false
      }

      const cancelStockItem = (stockItemSysId) => {
        const rIndex = diffState.rhs.findIndex(p => p.sys?.id === stockItemSysId)
        const lIndex = diffState.lhs.findIndex(p => p.sys?.id === stockItemSysId)
        diffState.rhs[rIndex] = cloneDeep(diffState.lhs[lIndex])
        editingStockItems.value.delete(stockItemSysId)
      }

      const toggleExpand = () => {
        expanded.value = !expanded.value
      }

      // TODO: update filter values
      const filterValues = [
        {
          val: 'everything',
          label: 'Everything'
        },
        {
          val: 'productWithMissingLink',
          label: 'Products with missing link'
        },
        {
          val: 'activeProducts',
          label: 'Active products'
        }
      ]
      setFilter('everything')

      // TODO: update filter values
      function applyFilter (item) {
        if (state.filterValue === 'everything' || !state.filterValue) {
          return true
        } else if (state.filterValue === 'activeProducts') {
          return item.active
        } else if (state.filterValue === 'productWithMissingLink') {
          return item.id === null || item.id === undefined
        }
      }

      const removeStockItem = (stockItem) => {
        if (!editing.value) editing.value = true
        toggleRemoval(stockItem)
      }

      const addableVariants = ref({})

      const filteredSupplierCatalogueStockItems = computed(() => {
        // return diffState.rhs
        // only works if the stock_item id for the catalogue_product is already resolved
        return diffState.rhs
          .filter((item, i, a) => {
            return (item.sys.id).includes(state.productQuery) ||
              applyQuery(item, i, a) ||
              // matches the parent item if a variant is matched
              state.variants[item.id]?.length && state.variants[item.id]
                .map(id => ({ id }))
                .some(v => applyQuery(v, i, a))
          })
          // filters out individual items that are shown as variants
          .filter(applyFilter)
      })

      const pagination = reactive({
        page: 1,
        pageSize: 9,
      })

      const setPage = (newPage) => {
        if (!editing.value && !editingStockItems.value.size) {
          pagination.page = newPage
        } else {
          $quasar.dialog({
            title: 'Unsaved changes will be lost',
            message: 'Continue without saving?',
            cancel: true,
            persistent: true
          }).onOk(() => {
            cancelEditing()
            pagination.page = newPage
          }).onCancel(() => {
            // console.log('>>>> Cancel')
          }).onDismiss(() => {
            // console.log('I am triggered on both OK and Cancel')
          })
        }
      }

      const expandable = computed(() => filteredSupplierCatalogueStockItems.value.length > 3)
      const maxPage = computed(() => Math.ceil(filteredSupplierCatalogueStockItems.value.length / pagination.pageSize))

      const paginatedStockItems = computed(() => {
        if (expanded.value) {
          return filteredSupplierCatalogueStockItems.value.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize)
        } else {
          return filteredSupplierCatalogueStockItems.value.slice((pagination.page - 1) * pagination.pageSize, (pagination.page - 1) * pagination.pageSize + 6)
        }
      })


      const createTabState = (ds = diffState.rhs) => !ds ? {} : Object.fromEntries(ds.map(s => ([s.sys.id, props.tabs.find(f => f.id === props.defaultTab)])))
      const tabState = ref(createTabState())

      function checkDeliveryThreshold (item: CtCatalogueStockItem): boolean {
        if (!item.deliverable_to) return false
        return date.getDateDiff(Date.now(), item.deliverable_to, 'days') < DELIVERY_WARN_THRESHOLD
      }

      function addItemPrice (item: CtCatalogueStockItem): void {
        item.sys.editing = true
        if (!item.price_list) item.price_list = [DEFAULT_PRICE_LIST_ITEM()]
        else item.price_list.push(DEFAULT_PRICE_LIST_ITEM())
      }

      async function updateItemPriceList (item: CtCatalogueStockItem): Promise<void> {
        item.sys.loading = true
        await requestHandler.patch(item.sys.id, { price_list: item.price_list })
        item.sys.loading = false
        item.sys.editing = false
      }

      const updateCatStockItem = (updatedCatStockItem) => {
        const index = diffState.rhs.findIndex(item => item.sys?.id === updatedCatStockItem.sys?.id)
        if (index >= 0) {
          diffState.rhs[index] = updatedCatStockItem
        }
      }

      const $router = useRouter()
      function toOrderOverview () {
        void $router.push({ name: 'SupplyOrders', params: { id: props.supplierCatalogue?.sys?.id } })
      }

      return {
        updatedItemsByUUID,
        ...toRefs(state),
        ...toRefs(saveState),
        ...toRefs(pagination),
        format,
        toOrderOverview,
        dateFormat: DATE_DISPLAY_FORMAT_SHORT,
        tabState,
        addItemPrice,
        updateItemPriceList,
        setPage,
        checkDeliveryThreshold,
        maxPage,
        filterValues,
        editing,
        editingStockItems,
        expandable,
        expanded,
        height,
        removeStockItem,
        toggleExpand,
        filteredStockItems: computed(() => { // filters out product that are already linked to a catalogue_product
          return state.products.filter(p => diffState.rhs.findIndex(i => i.id === p.sys.id) < 0)
        }),
        diffState,
        cancelEditing,
        save,
        cancelStockItem,
        paginatedStockItems,
        addVariant,
        getCatalogueVariantIndex,
        requestHandler,
        updateProductStore,
        updateLink,
        expandItem,
        // productRequestHandler,
        setQuery,
        setFilter,
        handleSave,
        openSearch,
        stockItemModel,
        select,
        toggleRemoval,
        addableVariants,
        linkStockItem: async (stockItem, selection) => {
          selection.forEach(select)
          state.editingItemLink = stockItem.sys.id
          updateLink()
          await save()
        },
        handleSelectionCommit: () => {
          !state.editingItemLink
            ? addStockItem()
            : updateLink()
        },
        updateCatStockItem,
        stockItems,
      }
    }
  })
